import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiStar, FiArrowRight, FiPhone } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { navigateToConsultation } from '../utils/navigation';

const SimpleConsultationPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if popup was recently dismissed
    const lastDismissed = localStorage.getItem('consultation_popup_dismissed');
    if (lastDismissed) {
      const dismissedTime = new Date(lastDismissed);
      const now = new Date();
      const hoursSince = (now - dismissedTime) / (1000 * 60 * 60);
      
      if (hoursSince < 24) {
        setIsDismissed(true);
        return;
      }
    }

    // Show popup after 5 seconds if not dismissed
    const timer = setTimeout(() => {
      if (!isDismissed) {
        setIsVisible(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isDismissed]);

  const handleClose = () => {
    console.log('Popup close button clicked'); // Debug log
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('consultation_popup_dismissed', new Date().toISOString());
  };

  const handleBookConsultation = () => {
    console.log('🔍 Popup consultation button clicked - navigating to consultation form');
    handleClose();
    navigateToConsultation(navigate);
  };

  if (isDismissed || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          e.preventDefault();
          handleClose();
        }}
      >
        <motion.div
          initial={{ 
            opacity: 0, 
            scale: 0.8,
            y: 50,
            rotateX: -15 
          }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            y: 0,
            rotateX: 0 
          }}
          exit={{ 
            opacity: 0, 
            scale: 0.8,
            y: 50,
            rotateX: -15 
          }}
          transition={{ 
            duration: 0.4, 
            ease: "easeOut",
            type: "spring",
            stiffness: 300,
            damping: 25
          }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-50 cursor-pointer"
            style={{ zIndex: 9999 }}
          >
            <FiX className="w-4 h-4 text-gray-600" />
          </button>

          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-primary-500 to-accent-500 p-6 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-white opacity-10 rounded-full translate-y-10 -translate-x-10"></div>
            
            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mb-4"
              >
                <FiCalendar className="w-6 h-6" />
              </motion.div>
              
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-xl font-bold mb-2"
              >
                Book Your Free Consultation!
              </motion.h3>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="text-white text-opacity-90 text-sm"
              >
                Start your transformation journey today
              </motion.p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <p className="text-gray-600 mb-6 leading-relaxed">
                Get personalized nutrition advice from our expert dietitian. Discover how we can help you achieve your health goals with a customized plan just for you.
              </p>

              {/* Features */}
              <div className="space-y-3 mb-6">
                {[
                  "Personalized nutrition plan",
                  "Expert dietitian consultation", 
                  "Goal-specific recommendations",
                  "24/7 WhatsApp support"
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <FiStar className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                >
                  <button
                    onClick={handleBookConsultation}
                    className="w-full btn-primary text-center block group relative overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      <FiCalendar className="w-4 h-4" />
                      <span>Book Free Consultation</span>
                      <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.4 }}
                >
                  <a
                    href="https://wa.me/919355353459?text=Hi! I would like to know more about your consultation services."
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClose}
                    className="w-full btn-secondary text-center block group"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <FiPhone className="w-4 h-4" />
                      <span>WhatsApp Us</span>
                    </span>
                  </a>
                </motion.div>
              </div>

              {/* Dismissal note */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0, duration: 0.4 }}
                className="text-xs text-gray-400 text-center mt-4"
              >
                This popup won't show again for 24 hours
              </motion.p>
            </motion.div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 to-accent-500"></div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SimpleConsultationPopup;